import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, FlaskConical } from "lucide-react";
import { api } from "@/lib/api";
import { listPosts } from "@/lib/fbLabApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const B = "/admin/notifications/bid-sms";
const err = (e) => e?.response?.data?.detail || "Something went wrong";
const STATUS = { sent: "Sent", failed: "Failed", queued: "Sending", not_configured: "Not sent: Twilio not connected",
  skipped_disabled: "Not sent: SMS off", skipped_no_phone: "Not sent: no test phone" };
const fmt = (s) => (s ? new Date(s).toLocaleString("en-CA", { timeZone: "America/Edmonton", dateStyle: "medium", timeStyle: "short" }) : "");

const History = ({ rows }) => (
  <ol data-testid="bid-sms-history" className="space-y-2 max-h-80 overflow-auto">
    {rows.length === 0 && <li className="text-xs text-stone-500">No texts yet.</li>}
    {rows.map((r) => (
      <li key={r.id} data-testid={`bid-sms-row-${r.id}`} className="border rounded-lg p-2 text-xs">
        <div className="flex flex-wrap gap-x-2 text-stone-500">
          <span className={r.status === "sent" ? "text-emerald-700 font-semibold" : r.status === "failed" ? "text-red-700 font-semibold" : "font-semibold"}>{STATUS[r.status] || r.status}</span>
          <span>{r.event === "test" ? "Test SMS" : r.simulated ? "Simulated bid" : "New bid"}</span>
          <span>{fmt(r.created_at)}</span>{r.to && <span>to {r.to}</span>}
        </div>
        <pre className="whitespace-pre-wrap font-sans text-stone-800 mt-1">{r.body}</pre>
        {r.error && <p className="text-red-700 mt-1">{r.error}</p>}
      </li>
    ))}
  </ol>
);

const Simulate = ({ onDone }) => {
  const [posts, setPosts] = useState([]);
  const [f, setF] = useState({ post_id: "", amount: "", bidder: "TEST Bidder" });
  useEffect(() => { listPosts().then(setPosts).catch(() => setPosts([])); }, []);
  const run = async () => {
    try {
      await api.post(`${B}/simulate`, { post_id: f.post_id || null, amount: Number(f.amount), bidder: f.bidder });
      toast.success("Simulated bid sent through the notification flow (no bid was saved)");
      onDone();
    } catch (e) { toast.error(err(e)); }
  };
  return (
    <div className="grid sm:grid-cols-4 gap-2 items-end">
      <select data-testid="bid-sms-sim-post" value={f.post_id} onChange={(e) => setF({ ...f, post_id: e.target.value })} className="h-10 border rounded-md px-2 text-sm sm:col-span-2">
        <option value="">Made-up horse and post</option>
        {posts.map((p) => <option key={p.id} value={p.id}>{p.horse_name || p.post_url}</option>)}
      </select>
      <Input data-testid="bid-sms-sim-amount" placeholder="Amount" inputMode="numeric" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} />
      <Input data-testid="bid-sms-sim-bidder" placeholder="Bidder" value={f.bidder} onChange={(e) => setF({ ...f, bidder: e.target.value })} />
      <Button data-testid="bid-sms-simulate" variant="outline" className="rounded-full sm:col-span-4 sm:w-fit" disabled={!(Number(f.amount) > 0)} onClick={run}>
        <FlaskConical className="h-4 w-4 mr-1" />Simulate a new bid
      </Button>
    </div>
  );
};

export const BidSmsSettings = () => {
  const [d, setD] = useState(null);
  const [phone, setPhone] = useState("");
  const load = () => api.get(B).then((r) => { setD(r.data); setPhone(r.data.test_phone || ""); }).catch(() => setD(false));
  useEffect(() => { load(); }, []);
  if (d === false) return null;
  if (!d) return null;
  const save = async (enabled) => {
    try { await api.put(B, { enabled, test_phone: phone || null }); toast.success("Bid SMS settings saved"); load(); } catch (e) { toast.error(err(e)); }
  };
  const test = async () => {
    try { const r = (await api.post(`${B}/test`)).data; r.status === "sent" ? toast.success("Test SMS sent") : toast.error(r.error || STATUS[r.status]); load(); } catch (e) { toast.error(err(e)); }
  };
  return (
    <section data-testid="bid-sms-settings" className="rounded-xl border border-stone-200 bg-white p-5 space-y-4">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold">Notifications</p>
        <h2 className="font-semibold text-stone-900 flex items-center gap-2"><MessageSquare className="h-4 w-4" />Bid SMS Notifications</h2>
        <p className="text-xs text-stone-500 mt-1">Phase 1 testing: a text goes only to the admin test number below whenever a sync records a new possible bid (still Pending Review). Never to buyers, sellers or trainers.</p>
        {!d.provider_connected && <p data-testid="bid-sms-provider-missing" className="text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mt-2">Twilio isn't connected yet, so texts are recorded below but not sent.</p>}
      </div>
      <div className="flex items-center gap-2">
        <Switch data-testid="bid-sms-enabled" checked={d.enabled} onCheckedChange={save} />
        <span className="text-sm text-stone-700 font-medium">{d.enabled ? "SMS notifications on" : "SMS notifications off"}</span>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input data-testid="bid-sms-phone" type="tel" placeholder="+1 403 555 0123" value={phone} onChange={(e) => setPhone(e.target.value)} className="sm:max-w-xs" />
        <Button data-testid="bid-sms-save-phone" variant="outline" className="rounded-full" onClick={() => save(d.enabled)}>Save number</Button>
        <Button data-testid="bid-sms-send-test" className="rounded-full bg-amber-800 hover:bg-amber-900 text-white" disabled={!d.test_phone} onClick={test}><Send className="h-4 w-4 mr-1" />Send Test SMS</Button>
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Simulate a new bid</h3>
        <p className="text-[11px] text-stone-500">Runs the real new-bid text using a made-up bid. Nothing is saved to bids or auction results.</p>
        <Simulate onDone={load} />
      </div>
      <div className="space-y-2">
        <h3 className="text-sm font-semibold">Notification history</h3>
        <History rows={d.history} />
      </div>
      <p className="text-[11px] text-stone-400">Coming later (not active): {d.events.filter((e) => !e.active).map((e) => e.label).join(" · ")}</p>
    </section>
  );
};
